import { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { ChevronLeft, ChevronRight, Mail, Bell, MessageSquare, Edit2, X, Bold, Italic, List, Image, Code } from 'lucide-react';

interface NotificationTemplate {
  id: string;
  name: string;
  description: string;
  audience: 'PROVIDER' | 'CUSTOMER' | 'BOTH';
  channels: ('email' | 'push' | 'sms')[];
  status: boolean;
  lastUpdated: string;
  subject?: string;
  bodyContent?: string;
}

interface EditingTemplate extends NotificationTemplate {
  channel?: string;
}

const NotificationsSettingsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [selectedAudience, setSelectedAudience] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState<EditingTemplate | null>(null);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await apiService.request('/admin/notifications', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const mapped = response.data.map((d: any) => ({
            id: d.id,
            name: d.name || d.event_name || '—',
            description: d.description || '—',
            audience: (d.audience || 'CUSTOMER').toUpperCase() as 'PROVIDER' | 'CUSTOMER' | 'BOTH',
            channels: d.channels ? (Array.isArray(d.channels) ? d.channels : [d.channels]) : ['email'],
            status: d.status !== false && d.is_active !== false,
            lastUpdated: d.updated_at ? new Date(d.updated_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) : '—',
            subject: d.subject || '',
            bodyContent: d.body_content || d.content || ''
          }));
          setTemplates(mapped);
        }
      } catch (error) {
        console.error('Failed to fetch notification templates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Mail className="w-4 h-4 text-gray-600" />;
      case 'push':
        return <Bell className="w-4 h-4 text-gray-600" />;
      case 'sms':
        return <MessageSquare className="w-4 h-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getAudienceLabel = (audience: string) => {
    switch (audience) {
      case 'PROVIDER':
        return 'PROVIDER';
      case 'CUSTOMER':
        return 'CUSTOMER';
      case 'BOTH':
        return 'BOTH';
      default:
        return audience;
    }
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(templates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTemplates = templates.slice(startIndex, endIndex);

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <p className="text-xs uppercase font-bold text-gray-500 tracking-wider">Settings › Notifications</p>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Notification Templates</h1>
          <p className="text-sm text-gray-600">
            Manage automated platform emails, push notifications, and SMS triggers
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-4">
            {/* All Channels Filter */}
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-600" />
              <select
                value={selectedChannel}
                onChange={(e) => setSelectedChannel(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white font-medium text-gray-700 hover:border-gray-400"
              >
                <option value="all">All Channels</option>
                <option value="email">Email</option>
                <option value="push">Push Notifications</option>
                <option value="sms">SMS</option>
              </select>
            </div>

            {/* All Audiences Filter */}
            <div className="flex items-center gap-2">
              <div className="w-4 h-4" />
              <select
                value={selectedAudience}
                onChange={(e) => setSelectedAudience(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white font-medium text-gray-700 hover:border-gray-400"
              >
                <option value="all">All Audiences</option>
                <option value="provider">Provider</option>
                <option value="customer">Customer</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>

          {/* Create Template Button */}
          <button
            onClick={() => {
              // Open the editor with a fresh blank template; existing JSX
              // hides the modal when editingTemplate is null.
              setEditingTemplate({
                id: 'new-' + Date.now(),
                name: 'New Template',
                channel: 'EMAIL',
                trigger: '',
                subject: '',
                body: '',
              } as any);
            }}
            className="px-4 py-2 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
          >
            <span>+</span>
            Create Template
          </button>
        </div>

        {/* Notifications Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-6 py-3">
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">Event Name</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">Audience</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">Channels</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">Last Updated</th>
                  <th className="text-center px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">Loading notification templates...</td>
                  </tr>
                ) : templates.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">No notification templates found</td>
                  </tr>
                ) : (
                  currentTemplates.map((template) => (
                    <tr key={template.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900 text-sm">{template.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded">
                          {getAudienceLabel(template.audience)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {template.channels.map((channel) => (
                            <div key={channel} title={channel} className="text-gray-400">
                              {getChannelIcon(channel)}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          className={`w-12 h-6 rounded-full transition-colors ${
                            template.status ? 'bg-teal-500' : 'bg-gray-300'
                          }`}
                        >
                          <div
                            className={`w-5 h-5 bg-white rounded-full transition-transform ${
                              template.status ? 'translate-x-6' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{template.lastUpdated}</td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => setEditingTemplate({ ...template, channel: 'email' })}
                          className="text-gray-400 hover:text-teal-600 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-600">
            Showing {startIndex + 1} of {templates.length} Templates
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-lg font-semibold transition-colors ${
                    currentPage === pageNum
                      ? 'bg-teal-700 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Edit Template Modal */}
        {editingTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-1">Template Configuration</p>
                  <h2 className="text-2xl font-bold text-gray-900">Edit Template: {editingTemplate.name}</h2>
                </div>
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 grid grid-cols-3 gap-6">
                {/* Left Column - Form */}
                <div className="col-span-2 space-y-6">
                  {/* Subject Line */}
                  <div>
                    <label className="block text-xs uppercase font-bold text-gray-500 mb-2">Subject Line</label>
                    <input
                      type="text"
                      defaultValue={editingTemplate.subject || ''}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Email subject..."
                    />
                  </div>

                  {/* Channel and Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase font-bold text-gray-500 mb-2">Channel</label>
                      <select className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                        <option>Email</option>
                        <option>Push Notification</option>
                        <option>SMS</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase font-bold text-gray-500 mb-2">Template Status</label>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() =>
                            setEditingTemplate((prev) =>
                              prev ? { ...prev, status: !prev.status } : prev
                            )
                          }
                          className={`w-12 h-6 rounded-full transition-colors ${
                            editingTemplate.status ? 'bg-teal-500' : 'bg-gray-300'
                          }`}
                        >
                          <div
                            className={`w-5 h-5 bg-white rounded-full transition-transform ${
                              editingTemplate.status ? 'translate-x-6' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                        <span className={`ml-3 text-sm font-semibold ${editingTemplate.status ? 'text-teal-600' : 'text-gray-500'}`}>
                          {editingTemplate.status ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Body Content */}
                  <div>
                    <label className="block text-xs uppercase font-bold text-gray-500 mb-2">Body Content</label>
                    
                    {/* Editor Toolbar */}
                    <div className="flex items-center gap-2 p-3 border border-gray-300 border-b-0 rounded-t-lg bg-gray-50">
                      <button className="p-2 hover:bg-gray-200 rounded transition-colors" title="Bold">
                        <Bold className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-200 rounded transition-colors" title="Italic">
                        <Italic className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-200 rounded transition-colors" title="List">
                        <List className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-200 rounded transition-colors" title="Image">
                        <Image className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-2 hover:bg-gray-200 rounded transition-colors" title="Code">
                        <Code className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>

                    {/* Text Area */}
                    <textarea
                      defaultValue={editingTemplate.bodyContent || ''}
                      className="w-full px-4 py-3 border border-gray-300 rounded-b-lg text-sm font-mono resize-none"
                      rows={8}
                      placeholder="Email body content..."
                    />
                    <p className="text-xs text-gray-500 mt-2">314 CHARACTERS / 1500 MAX</p>
                  </div>
                </div>

                {/* Right Column - Dynamic Variables */}
                <div className="col-span-1">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start gap-2 mb-3">
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center mt-0.5">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">Dynamic Variables</h3>
                    </div>
                    <p className="text-xs text-gray-600 mb-4">
                      Click a variable to copy it to your clipboard. Use these to personalize your messaging.
                    </p>

                    <div className="space-y-2">
                      {[
                        '{{customer_name}}',
                        '{{job_id}}',
                        '{{payout_amount}}',
                        '{{scheduled_time}}',
                        '{{location}}',
                        '{{deadline}}'
                      ].map((variable) => (
                        <button
                          key={variable}
                          onClick={() => navigator.clipboard.writeText(variable)}
                          className="w-full text-left px-3 py-2 bg-white border border-gray-300 rounded font-mono text-xs text-teal-600 hover:bg-teal-50 transition-colors"
                        >
                          {variable}
                        </button>
                      ))}
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 rounded text-xs text-blue-900 border border-blue-200">
                      <p className="font-medium mb-1">ℹ️ Template Support</p>
                      <p>Templates support basic HTML tags and Liquid logic for advanced conditional rendering.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="px-6 py-2 text-gray-700 font-medium hover:text-gray-900"
                >
                  Cancel
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      const to = window.prompt('Send a test notification to which email address?');
                      if (!to || !to.trim()) return;
                      try {
                        const res: any = await apiService.request('/admin/notifications/test', 'POST', {
                          templateId: editingTemplate?.id,
                          recipient: to.trim(),
                        });
                        if (res?.success) alert('Test notification queued for ' + to);
                        else alert('Error: ' + (res?.message || 'Failed to send test'));
                      } catch (e: any) {
                        alert('Failed: ' + (e?.message || 'Network error'));
                      }
                    }}
                    className="px-6 py-2 border border-gray-300 hover:bg-gray-50 text-gray-900 font-semibold rounded-lg transition-colors"
                  >
                    Send Test...
                  </button>
                  <button
                    onClick={async () => {
                      if (!editingTemplate) return;
                      try {
                        const res: any = await apiService.request(
                          `/admin/notifications/${editingTemplate.id}`,
                          'PUT',
                          {
                            name: editingTemplate.name,
                            audience: editingTemplate.audience,
                            channels: editingTemplate.channels,
                            status: editingTemplate.status,
                            subject: editingTemplate.subject,
                            body_content: editingTemplate.bodyContent,
                          }
                        );
                        if (res?.success) {
                          // Reflect changes in the local list.
                          setTemplates((list) =>
                            list.map((t) => (t.id === editingTemplate.id ? { ...t, ...editingTemplate, lastUpdated: new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) } : t))
                          );
                          setEditingTemplate(null);
                        } else {
                          alert('Error: ' + (res?.message || 'Failed to save'));
                        }
                      } catch (e: any) {
                        alert('Failed: ' + (e?.message || 'Network error'));
                      }
                    }}
                    className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white font-semibold rounded-lg transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default NotificationsSettingsPage;
